import { memo, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { camelCase } from 'lodash/string';
import getStatusBadge, { getTagsColor } from '@shared/lib/getStatusBadge';
import { renderLocalizedData } from '@shared/lib/localize';
import { RESOURCE_ICON_LIST_FOR_TABLE } from '@entities/resource';
// Badge primitive still lives in legacy. The FSD `@shared/ui/Badge` is
// the canonical primitive; `_legacy/components/Shared/Badge` is a thin
// wrapper that maps the legacy `BADGES_COLOR_TYPES` enum into FSD
// tones so existing callers (like this one) keep working until the
// badge-API migration is finished.
// eslint-disable-next-line no-restricted-imports
import { BADGES_COLOR_TYPES, SimpleBadge } from '_legacy/components/Shared/Badge';
// `OverflownText` is also still legacy-only; it deserves its own FSD
// migration but that's out of scope for this pass.
// eslint-disable-next-line no-restricted-imports
import OverflownText from '_legacy/components/Shared/OverflownText';
import ViewedTableCell from './ViewedTableCell';
import InfoPaneWrapper from './InfoPaneWrapper';

/**
 * `CustomTableCell` — branchy renderer used by the FSD Table when a
 * column requires more than plain text: badges (status / gender /
 * entity type), tag bubbles, resource icons, info-pane links, viewed-
 * state row tinting.
 *
 * Lifted from `_legacy/components/Shared/Table/widgets/Cell/
 * TableCell.jsx`. No logic changes:
 *   - same `compare`-based memoization (`areEqual`) on the outer
 *     `CustomTableCell` and `_BaseTableCell`,
 *   - same `withBubbles` / `withBubble` / `infoPanePage` / `isStatus`
 *     / `withIcon` switch,
 *   - same `camelCase(rawValue)` lookup for status badges (so
 *     server-side `IN_PROGRESS` becomes the `inProgress` token the
 *     `getStatusBadge` map uses),
 *   - sibling imports retargeted at `./ViewedTableCell` and
 *     `./InfoPaneWrapper` (both also moved into this folder).
 */

const Cell = styled('div')({
  textOverflow: 'ellipsis',
  maxWidth: '100%',
  display: 'flex',
  // Centre the content when a sibling cell wraps and the row grows —
  // `pre-line` below lets long values (a program template name, an
  // assessment title) take two lines, and without this every other
  // cell in that row sat at the top edge while the badge floated
  // somewhere in the middle.
  alignItems: 'center',
  width: '100%',
  height: '100%',
  backgroundColor: 'transparent',
  transition: 'background-color .3s ease-in, font-weight .3s ease-in',
  overflow: 'hidden',
  gap: 5,
  whiteSpace: 'pre-line',
});

const ResourceType = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const ResourceTypePadding = styled('div')({
  width: 12,
});

// Shallow equality on the props that actually drive a re-render. The
// legacy version used `fast-json-patch.compare` which produces a full
// RFC-6902 diff (10–100x slower than shallow compare) and walks JSX
// children — `renderedCellValue` is often a React element whose
// internal fiber refs trip the diff into reporting noise patches,
// effectively defeating the memoization. None of the props below are
// nested objects we mutate in place; reference equality is the right
// test.
function areEqual(prevProps, nextProps) {
  return (
    prevProps.dataKey === nextProps.dataKey &&
    prevProps.withBubble === nextProps.withBubble &&
    prevProps.withBubbles === nextProps.withBubbles &&
    prevProps.row === nextProps.row &&
    prevProps.canManageViewed === nextProps.canManageViewed &&
    prevProps.renderedCellValue === nextProps.renderedCellValue &&
    prevProps.infoPanePage === nextProps.infoPanePage &&
    prevProps.hasManageViewed === nextProps.hasManageViewed &&
    prevProps.selectorOptionsFromApi === nextProps.selectorOptionsFromApi
  );
}

const CustomTableCell = memo(function CustomTableCell(prop) {
  const {
    dataKey,
    withBubble,
    withBubbles,
    row,
    canManageViewed,
    renderedCellValue,
    infoPanePage,
    hasManageViewed,
    selectorOptionsFromApi,
  } = prop;
  return hasManageViewed ? (
    <ViewedTableCell row={row} canManageViewed={canManageViewed}>
      <_BaseTableCell
        dataKey={dataKey}
        withBubble={withBubble}
        withBubbles={withBubbles}
        row={row}
        renderedCellValue={renderedCellValue}
        infoPanePage={infoPanePage}
        selectorOptionsFromApi={selectorOptionsFromApi}
      />
    </ViewedTableCell>
  ) : (
    <_BaseTableCell
      dataKey={dataKey}
      withBubble={withBubble}
      withBubbles={withBubbles}
      row={row}
      renderedCellValue={renderedCellValue}
      infoPanePage={infoPanePage}
      selectorOptionsFromApi={selectorOptionsFromApi}
    />
  );
}, areEqual);

const _BaseTableCell = memo(function _BaseTableCell({
  dataKey,
  withBubble,
  row,
  renderedCellValue: currentValue,
  infoPanePage,
  withBubbles,
  selectorOptionsFromApi,
}) {
  if (withBubbles) {
    const rawValues = row?._rawValues?.[dataKey];
    return (
      <OverflownText
        data={currentValue.map((item, index) => {
          const rawItem = rawValues?.[index] || item;
          let colorType = getTagsColor(rawItem);
          if (selectorOptionsFromApi) {
            const color = selectorOptionsFromApi.find(i => i.value === rawItem)?.color;
            colorType = BADGES_COLOR_TYPES[color];
          }
          return <SimpleBadge label={item} colorType={colorType} variant={'default'} />;
        })}
      />
    );
  }

  if (infoPanePage) {
    return (
      <InfoPaneWrapper currentValue={currentValue} row={row} infoPanePage={infoPanePage} />
    );
  }

  const isStatus = useMemo(
    () =>
      dataKey.toLowerCase().includes('status') ||
      dataKey === 'entityType' ||
      dataKey === 'patientGender',
    [dataKey]
  );
  const withIcon = dataKey === 'resourceName';

  const isNonTextCell = isStatus || withIcon || withBubble;

  if (!isNonTextCell) {
    return <Cell>{currentValue}</Cell>;
  }

  const rawValue = row?._rawValues?.[dataKey] || currentValue;

  const data = useMemo(() => {
    if (isStatus || withBubble) {
      return (
        <SimpleBadge
          label={currentValue}
          colorType={
            isStatus && dataKey !== 'entityType'
              ? getStatusBadge(camelCase(rawValue))
              : getStatusBadge(rawValue)
          }
        />
      );
    }
    if (withIcon) {
      return (
        <ResourceType>
          {RESOURCE_ICON_LIST_FOR_TABLE[currentValue.resourceType]}
          <ResourceTypePadding />
          {renderLocalizedData(currentValue.resourceName ?? '-')}
        </ResourceType>
      );
    }
    return null;
  }, [currentValue, rawValue]);

  return useMemo(() => <Cell>{data}</Cell>, [data]);
});

export default CustomTableCell;
